import { useEffect, useRef } from "react"

const PARTICLE_COUNT = 1400
// The opening burst eases each particle toward its burst distance as
// 1 - e^(-t/τ), so it leaves the center fast and glides. τ varies per particle
// so the cloud thins out unevenly instead of expanding as one ring.
const MIN_GLIDE_MS = 700
const MAX_GLIDE_MS = 1600
// After this many of the longest glides, e^(-t/τ) is small enough that the
// burst adds nothing visible.
const BURST_SETTLED_GLIDES = 20
// After the burst every particle keeps drifting outward. Speed grows with
// distance, so the core stays dense while the edge streams off screen.
const MIN_DRIFT_PER_SECOND = 0.02
const MAX_DRIFT_PER_SECOND = 0.05
// A particle that leaves the canvas re-enters at the center and fades in.
const RESPAWN_FADE_MS = 1200
// Caps the step after a backgrounded tab resumes so the cloud does not jump.
const MAX_FRAME_MS = 50
// The canvas spans the viewport, so an uncapped high-DPI backing store can run
// to hundreds of megabytes. Sub-pixel dots gain nothing past 2x.
const MAX_DEVICE_PIXEL_RATIO = 2

interface Particle {
  /** Unit direction of travel from the burst center. */
  directionX: number
  directionY: number
  /** Distance from the center, as a fraction of the cloud radius. */
  distance: number
  burstDistance: number
  glideMs: number
  driftPerSecond: number
  radius: number
  alpha: number
  /** Time since the particle last (re)spawned, for the fade-in. */
  ageMs: number
  respawned: boolean
}

function createParticle(): Particle {
  const angle = Math.random() * Math.PI * 2
  return {
    directionX: Math.cos(angle),
    directionY: Math.sin(angle),
    distance: 0,
    // The power curve packs most particles near the center and lets a few fly
    // far out.
    burstDistance: Math.random() ** 1.6,
    glideMs: MIN_GLIDE_MS + Math.random() * (MAX_GLIDE_MS - MIN_GLIDE_MS),
    driftPerSecond:
      MIN_DRIFT_PER_SECOND +
      Math.random() * (MAX_DRIFT_PER_SECOND - MIN_DRIFT_PER_SECOND),
    radius: 0.6 + Math.random() * 1.1,
    alpha: 0.25 + Math.random() * 0.55,
    ageMs: 0,
    respawned: false,
  }
}

function respawn(particle: Particle): void {
  const angle = Math.random() * Math.PI * 2
  particle.directionX = Math.cos(angle)
  particle.directionY = Math.sin(angle)
  particle.distance = Math.random() * 0.05
  particle.burstDistance = 0
  particle.ageMs = 0
  particle.respawned = true
}

// Falls back to window resizes where ResizeObserver is unavailable, so the
// error fallback itself can never throw.
function observeResize(element: Element, onResize: () => void): () => void {
  if (typeof ResizeObserver === "undefined") {
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }
  const observer = new ResizeObserver(onResize)
  observer.observe(element)
  return () => observer.disconnect()
}

/**
 * Decorative particle cloud for the error fallback: bursts out from the center
 * on mount, then keeps expanding outward forever, recycling particles that
 * leave the canvas back through the center. Fills its positioned parent and
 * draws in the canvas's CSS `color`. While `isStill`, the cloud holds where it
 * is; a cloud that mounts still skips the burst.
 */
export function ErrorParticleBurst({ isStill }: { isStill: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const isStillRef = useRef(isStill)
  const syncMotionRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas?.getContext("2d")
    if (!canvas || !context) {
      return
    }

    const particles = Array.from({ length: PARTICLE_COUNT }, createParticle)
    if (isStillRef.current) {
      for (const particle of particles) {
        particle.distance = particle.burstDistance
      }
    }
    let width = 0
    let height = 0

    const draw = () => {
      context.clearRect(0, 0, width, height)
      context.fillStyle = getComputedStyle(canvas).color
      const centerX = width / 2
      const centerY = height / 2
      // The cloud is wider than tall so it fills a landscape viewport.
      const radiusX = Math.min(width * 0.48, height * 0.8)
      const radiusY = Math.min(height * 0.46, width * 0.48)
      for (const particle of particles) {
        const x = centerX + particle.directionX * particle.distance * radiusX
        const y = centerY + particle.directionY * particle.distance * radiusY
        const margin = particle.radius
        if (
          x < -margin ||
          x > width + margin ||
          y < -margin ||
          y > height + margin
        ) {
          respawn(particle)
          continue
        }
        const fade = particle.respawned
          ? Math.min(particle.ageMs / RESPAWN_FADE_MS, 1)
          : 1
        context.globalAlpha = particle.alpha * fade
        context.beginPath()
        context.arc(x, y, particle.radius, 0, Math.PI * 2)
        context.fill()
      }
    }

    const step = (elapsedMs: number, frameMs: number) => {
      for (const particle of particles) {
        // Derivative of burstDistance * (1 - e^(-t/τ)), so the burst and the
        // drift add up without a hand-off.
        const burstSpeed =
          (particle.burstDistance / particle.glideMs) *
          Math.exp(-elapsedMs / particle.glideMs)
        const driftSpeed =
          (particle.driftPerSecond / 1000) * (0.35 + particle.distance)
        particle.distance += (burstSpeed + driftSpeed) * frameMs
        particle.ageMs += frameMs
      }
    }

    const resize = () => {
      const devicePixelRatio = Math.min(
        window.devicePixelRatio || 1,
        MAX_DEVICE_PIXEL_RATIO,
      )
      width = canvas.clientWidth
      height = canvas.clientHeight
      canvas.width = Math.round(width * devicePixelRatio)
      canvas.height = Math.round(height * devicePixelRatio)
      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0)
      draw()
    }
    // Observes the canvas itself: the app shell can resize the content region
    // (Sofia docking or undocking) without a window resize.
    resize()
    const unobserveResize = observeResize(canvas, resize)

    let animationFrame = 0
    // Counts only time spent moving. A cloud that mounts still is already
    // spread out, so its clock starts past the burst and Play adds only drift.
    let movingMs = isStillRef.current ? MAX_GLIDE_MS * BURST_SETTLED_GLIDES : 0
    let previous = 0
    const frame = (timestamp: number) => {
      const frameMs = previous
        ? Math.min(timestamp - previous, MAX_FRAME_MS)
        : 0
      previous = timestamp
      movingMs += frameMs
      step(movingMs, frameMs)
      draw()
      animationFrame = requestAnimationFrame(frame)
    }
    // Freezes the cloud where it is when motion stops, and resumes the drift
    // when it starts again.
    const syncMotion = () => {
      cancelAnimationFrame(animationFrame)
      previous = 0
      if (!isStillRef.current) {
        animationFrame = requestAnimationFrame(frame)
      }
    }
    syncMotion()
    syncMotionRef.current = syncMotion

    return () => {
      cancelAnimationFrame(animationFrame)
      unobserveResize()
      syncMotionRef.current = null
    }
  }, [])

  useEffect(() => {
    isStillRef.current = isStill
    syncMotionRef.current?.()
  }, [isStill])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full text-coval-text-muted"
    />
  )
}
